import './styles.css';
import { api, setToken, getToken } from './api.ts';
import {
  anmelden,
  anmeldenMitUebergabe,
  currentVault,
  lockVault,
  setupWithToken,
  wiederherstellen,
} from './crypto/vault.ts';
import { aufUebergabeWarten, istEingebettet } from './uebergabe.ts';
import { importPublicKey, type KeyAlgorithm } from './crypto/keypair.ts';
import { fromBase64 } from './crypto/primitives.ts';
import { uploadDocument } from './documents.ts';
import { kreisCacheLeeren } from './schluesselkreise.ts';
import { safesVerwerfen } from './safes.ts';
import { einladungAnnehmen, einladungAusAdresse } from './einladung.ts';
import { notfallzugriffWidersprechen } from './vertrauensperson.ts';
import { ablegeFeld, clear, el, formatDate, hinweis, karte, passwortfeld } from './ui.ts';
import {
  aufbereiten,
  beendeAufbereitung,
  datenVerwerfen,
  fehlertext,
  geoeffneteUrlsWiderrufen,
  kopfzeile,
  registriereAbmelden,
  registriereOrganisationsWechsel,
  root,
  stapelMeldung,
  stapelVerarbeiten,
  state,
  ticket,
  zeigeSchritt,
  type Me,
  type StapelErgebnis,
} from './ansichten/gemeinsam.ts';
import { renderStart } from './ansichten/start.ts';
import { renderDokumente } from './ansichten/dokumente.ts';
import { renderFreigaben, renderProtokollVoll } from './ansichten/freigaben.ts';
import { renderKonto } from './ansichten/konto.ts';

/**
 * Einstieg und Rahmen der Anwendung: Anmeldung, Einrichtung,
 * Wiederherstellung, Blindablage und die Verteilung auf die Ansichten.
 *
 * Die Ansichten selbst (Startseite, Dokumente, Freigaben, Mein Konto) liegen
 * unter `ansichten/` – erreichbar über das Menü in der Kopfzeile, geroutet
 * über den Anker in der Adresse (`#/…`). Vor- und Zurück im Browser
 * funktionieren dadurch von selbst.
 */

// ---------------------------------------------------------------------------
// Parameter aus der Adresse (werden nach dem Lesen bereinigt)
// ---------------------------------------------------------------------------

/**
 * Einrichtungslink aus der automatisch versendeten Mail: E-Mail und Code
 * stehen schon in der Adresse, damit nur noch das Passwort fehlt.
 *
 * Genau wie beim Ticketbezug (gemeinsam.ts) ist das keine Berechtigung, nur
 * eine Vorbefüllung — geprüft wird der Code weiterhin ausschließlich vom
 * Server beim Einlösen (`/api/auth/setup`).
 */
function einrichtungsLink(): { email: string; code: string } | null {
  const parameter = new URLSearchParams(window.location.search);
  const email = parameter.get('einrichten_email')?.trim();
  const code = parameter.get('einrichten_code')?.trim();
  if (!email || !code) return null;
  return { email, code };
}

const einrichtung = einrichtungsLink();

/**
 * Ziel einer Blindablage aus dem Link des Verwaltungssystems (Konzept 6.6.2).
 *
 * Der Buddy landet damit nicht auf der allgemeinen Übersicht, sondern direkt
 * auf einer Maske für genau diesen einen Kunden — die Auswahl aus einer
 * Liste entfällt, mit ihr die Gefahr, aus Versehen den falschen Kunden zu
 * treffen (siehe Warnhinweis in `renderBlindablage`).
 *
 * Wie beim Ticketbezug ist die Kennung selbst kein Nachweis — sie kommt
 * aus der Adresszeile. Berechtigt wird ausschließlich serverseitig: Den
 * öffentlichen Schlüssel liefert `/api/keys/customer/:id/public` nur aus,
 * wenn der angemeldete Buddy diesem Kunden auch tatsächlich zugeordnet ist.
 */
interface BlindablageZiel {
  customerId: string;
  name: string | null;
  /**
   * Einmal-Ticket aus dem Verwaltungssystem (Konzept 6.6.2). Damit meldet
   * `start()` den Buddy automatisch an, ohne dass er sein Passwort erneut
   * eingibt — siehe `/api/auth/blindablage-einloesen`. Fehlt es (etwa beim
   * Testen mit von Hand gebauter Adresse), landet der Buddy stattdessen auf
   * der normalen Anmeldung und danach automatisch hier.
   */
  einloeseToken: string | null;
}

function blindablageZiel(): BlindablageZiel | null {
  const parameter = new URLSearchParams(window.location.search);
  const customerId = parameter.get('blindablage')?.trim();
  if (!customerId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(customerId)) {
    return null;
  }
  const name = parameter.get('name')?.trim().slice(0, 200) || null;
  const einloeseToken = parameter.get('blindablage_token')?.trim() || null;
  return { customerId, name, einloeseToken };
}

const blindablageZielKunde = blindablageZiel();

/**
 * Widerspruchslink aus der Notfallzugriff-Mail (Entscheidung 10.08.2026).
 *
 * Führt auf eine eigenständige, unauthentifizierte Seite (`renderNotfallWiderspruch`)
 * — der Kunde muss sich nicht anmelden, der Token aus der Mail ist der
 * einzige Nachweis, genau wie beim Widerruf der Konto-Selbstlöschung im
 * Verwaltungssystem.
 */
function notfallWiderspruchToken(): string | null {
  const parameter = new URLSearchParams(window.location.search);
  return parameter.get('notfall_widerspruch')?.trim() || null;
}

const notfallWiderspruch = notfallWiderspruchToken();

/**
 * Einmal-Token aus der Admin-Kachel des Verwaltungssystems (26.08.2026):
 * meldet das Personal-Konto ohne Passwort an — gleicher Einlöseweg wie die
 * Blindablage, nur ohne Kundenbezug. Danach normale Personal-Oberfläche.
 */
function personalToken(): string | null {
  const parameter = new URLSearchParams(window.location.search);
  return parameter.get('personal_token')?.trim() || null;
}

const personalEinloeseToken = personalToken();

/**
 * Einladung in eine weitere Organisation (Migration 007/012, Schlüsselkreise).
 * Anders als bei den übrigen Links hier steht das Geheimnis im Fragment-Anker
 * (`#…`), nicht in der Query — Fragmente verlassen den Browser nie über einen
 * HTTP-Request (siehe `backend/src/routes/einladungen.ts`). Der Anker ist bei
 * diesem einen Link deshalb bewusst KEINE Route und muss vor dem üblichen
 * `replaceState` unten gelesen und separat entfernt werden, sonst bliebe das
 * Geheimnis in der Adresszeile stehen oder würde vom Router als Route
 * missverstanden.
 */
const einladungZiel = einladungAusAdresse();
if (einladungZiel) {
  window.history.replaceState({}, '', window.location.pathname);
}

if (ticket || einrichtung || blindablageZielKunde || notfallWiderspruch || personalEinloeseToken) {
  // Adresse bereinigen, Zustand behalten (der Anker mit der Route bleibt).
  window.history.replaceState({}, '', window.location.pathname + window.location.hash);
}

// ---------------------------------------------------------------------------
// Routing: Anker in der Adresse → Ansicht
// ---------------------------------------------------------------------------

async function zeigeAnsicht(): Promise<void> {
  // Ohne Sitzung rendern die Anmelde-Abläufe selbst; ein Ankerwechsel auf
  // der Anmeldeseite (etwa nach dem Abmelden) darf nichts überschreiben.
  if (!state.me) return;

  if (state.me.role === 'admin') {
    renderAdmin();
    return;
  }

  // Kunden und Buddys brauchen den entsperrten Schlüssel. Ist er weg (das
  // passiert nur nach Programmierfehlern – normale Wege führen über die
  // Anmeldung), zurück zur Anmeldung statt leerer Ansichten.
  if (currentVault() === null) {
    renderLogin('Bitte melden Sie sich erneut an, um Ihre Dokumente aufzuschließen.');
    return;
  }

  const hash = window.location.hash;

  if (state.me.role === 'buddy') {
    if (hash === '#/hilfe') renderFaq();
    else await renderDokumente();
    return;
  }

  switch (hash) {
    case '#/dokumente':
      await renderDokumente();
      break;
    case '#/freigaben':
      await renderFreigaben();
      break;
    case '#/protokoll':
      await renderProtokollVoll();
      break;
    case '#/konto':
      await renderKonto();
      break;
    case '#/hilfe':
      renderFaq();
      break;
    default:
      await renderStart();
      break;
  }
}

window.addEventListener('hashchange', () => void zeigeAnsicht());

/** Zur Route wechseln – rendert auch dann, wenn der Anker schon stimmt. */
function navigiereZu(hash: string): void {
  if (window.location.hash === hash) {
    void zeigeAnsicht();
  } else {
    window.location.hash = hash;
  }
}

// ---------------------------------------------------------------------------
// Anmeldung
// ---------------------------------------------------------------------------

function renderLogin(meldung?: string): void {
  clear(root);
  root.append(
    el('h1', {}, el('span', { class: 'tb-wordmark' }, 'KF'), ' Akten-Safe'),
    el('p', { class: 'leise' }, 'Ihre Unterlagen, verschlüsselt auf Ihrem eigenen Gerät.'),
    karte(
      el('h2', {}, 'Anmelden'),
      meldung ? hinweis(meldung, 'warnung') : null,
      el('label', { for: 'email' }, 'E-Mail-Adresse'),
      el('input', { type: 'email', id: 'email', autocomplete: 'username' }),
      el('label', { for: 'passwort' }, 'Ihr Passwort'),
      el('input', { type: 'password', id: 'passwort', autocomplete: 'current-password' }),
      el(
        'p',
        { class: 'leise' },
        'Ihr Passwort verlässt dieses Gerät nicht. Es wird hier in zwei ' +
          'getrennte Werte umgerechnet – einer weist Sie gegenüber uns ' +
          'aus, der andere schließt Ihre Dokumente auf und bleibt bei Ihnen.',
      ),
      el(
        'div',
        { class: 'knopfreihe' },
        el('button', { id: 'anmelden', onclick: anmeldenKlick }, 'Anmelden'),
        el(
          'button',
          { class: 'sekundaer', onclick: () => renderEinrichtung() },
          'Konto einrichten',
        ),
      ),
      el(
        'p',
        { class: 'leise' },
        el(
          'a',
          { href: '#', onclick: (e: Event) => { e.preventDefault(); renderWiederherstellung(); } },
          'Passwort vergessen? Mit Wiederherstellungs-Code neu setzen',
        ),
      ),
    ),
  );

  root.querySelector('#passwort')?.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key === 'Enter') void anmeldenKlick();
  });
}

async function anmeldenKlick(): Promise<void> {
  const email = root.querySelector<HTMLInputElement>('#email')!.value.trim();
  const password = root.querySelector<HTMLInputElement>('#passwort')!.value;
  const button = root.querySelector<HTMLButtonElement>('#anmelden')!;

  button.disabled = true;
  button.textContent = 'Wird geprüft …';
  try {
    // Anmelden und Aufschließen in einem Schritt – eine einzige Eingabe.
    const account = await anmelden(email, password);
    state.me = await api.get<Me>('/api/auth/me');
    if (account.role === 'buddy' && blindablageZielKunde) {
      await renderBlindablage(blindablageZielKunde);
    } else if (einladungZiel && account.role === 'customer') {
      await einladungNachAnmeldungAnnehmen();
    } else {
      await zeigeAnsicht();
    }
  } catch (error) {
    renderLogin(fehlertext(error));
  }
}

/**
 * Läuft genau einmal, direkt nach einer Anmeldung über einen Einladungslink
 * (siehe `einladungZiel` oben). Zeigt eine eigene Zwischenseite statt
 * stillschweigend in die normale Ansicht zu springen — sonst merkt der
 * Eingeladene gar nicht, dass gerade etwas Wichtiges passiert ist.
 */
async function einladungNachAnmeldungAnnehmen(): Promise<void> {
  if (!einladungZiel) return;
  clear(root);
  root.append(
    kopfzeile(),
    karte(el('h2', {}, 'Einladung wird angenommen'), el('p', { class: 'loading' }, 'Bitte warten …')),
  );

  try {
    await einladungAnnehmen(einladungZiel.id, einladungZiel.secretBase64);
    clear(root);
    root.append(
      kopfzeile(),
      karte(
        el('h2', {}, 'Einladung angenommen'),
        hinweis(
          'Sie haben jetzt Zugriff auf den neuen Safe. Er erscheint ab sofort neben Ihren anderen ' +
            'Safes – auf der Startseite wählen Sie, wo Sie suchen möchten.',
          'erfolg',
        ),
        el(
          'div',
          { class: 'knopfreihe' },
          el('button', { onclick: () => { window.location.hash = '#/'; void zeigeAnsicht(); } }, 'Weiter'),
        ),
      ),
    );
  } catch (error) {
    clear(root);
    root.append(
      kopfzeile(),
      karte(
        el('h2', {}, 'Einladung konnte nicht angenommen werden'),
        hinweis(fehlertext(error), 'warnung'),
        el(
          'div',
          { class: 'knopfreihe' },
          el(
            'button',
            { onclick: () => { window.location.hash = '#/'; void zeigeAnsicht(); } },
            'Weiter zum Akten-Safe',
          ),
        ),
      ),
    );
  }
}

async function ladeKonto(): Promise<void> {
  // Nach einem Neuladen ist zwar die Sitzung noch gültig, der
  // Verschlüsselungsschlüssel aber weg – er lebt nur im Arbeitsspeicher.
  state.me = await api.get<Me>('/api/auth/me');
  if (state.me.role === 'admin') {
    renderAdmin();
  } else {
    setToken(null);
    state.me = null;
    renderLogin(
      einladungZiel
        ? 'Sie wurden zu einer Organisation eingeladen. Bitte melden Sie sich erneut an, um ' +
            'die Einladung anzunehmen.'
        : 'Bitte melden Sie sich erneut an, um Ihre Dokumente aufzuschließen.',
    );
  }
}

// ---------------------------------------------------------------------------
// Ersteinrichtung mit Code
// ---------------------------------------------------------------------------

function renderEinrichtung(meldung?: string, vorbefuellt?: { email: string; code: string }): void {
  clear(root);
  root.append(
    el('h1', {}, el('span', { class: 'tb-wordmark' }, 'KF'), ' Akten-Safe'),
    karte(
      el('h2', {}, 'Konto einrichten'),
      el(
        'p',
        {},
        vorbefuellt
          ? 'Sie kommen über Ihren Einladungslink. Legen Sie jetzt Ihr Passwort fest.'
          : 'Sie haben von Ihrem Ansprechpartner einen Einrichtungs-Code bekommen. Damit ' +
              'legen Sie jetzt Ihr Passwort fest.',
      ),
      meldung ? hinweis(meldung, 'warnung') : null,
      el('label', { for: 'e-email' }, 'E-Mail-Adresse'),
      el('input', {
        type: 'email',
        id: 'e-email',
        autocomplete: 'username',
        value: vorbefuellt?.email ?? '',
        ...(vorbefuellt ? { readonly: true } : {}),
      }),
      el('label', { for: 'e-code' }, 'Einrichtungs-Code'),
      el('input', {
        type: 'text',
        id: 'e-code',
        placeholder: 'ABCD-EFGH-JKLM',
        value: vorbefuellt?.code ?? '',
        ...(vorbefuellt ? { readonly: true } : {}),
      }),
      // Bis zum 27.08.2026 stand hier das Gegenteil: „ein neues, eigenes
      // Passwort, unabhängig vom Kundenportal". Das stimmte, solange beide
      // Systeme getrennt rechneten. Seit das Kundenportal denselben
      // Hauptschlüssel ableitet, ist ein abweichendes Passwort der Grund,
      // warum der Akten-Safe sich nicht aus dem Portal heraus öffnet —
      // und niemand käme von selbst darauf, dass das zusammenhängt.
      hinweis(
        'Haben Sie schon ein Passwort für Ihren Kundenbereich? Dann nehmen Sie ' +
          'hier bitte dasselbe. Ihr Akten-Safe geht dann mit einem Klick aus ' +
          'dem Kundenbereich auf, ohne dass Sie sich ein zweites Mal anmelden. ' +
          'Ein anderes Passwort ist auch in Ordnung — dann fragt der ' +
          'Akten-Safe Sie jedes Mal eigens danach.',
      ),
      el('label', { for: 'e-pw1' }, 'Neues Passwort wählen'),
      passwortfeld({ id: 'e-pw1', autocomplete: 'new-password' }),
      el('label', { for: 'e-pw2' }, 'Passwort wiederholen'),
      passwortfeld({ id: 'e-pw2', autocomplete: 'new-password' }),
      hinweis(
        'Mit diesem einen Passwort melden Sie sich an UND schließen Ihre ' +
          'Dokumente auf. Es verlässt Ihr Gerät nie – auch wir kennen es ' +
          'nicht. Geht es verloren, kommen Sie nur noch mit dem ' +
          'Wiederherstellungs-Code an Ihre Unterlagen, den Sie gleich bekommen.',
        'warnung',
      ),
      el('div', { id: 'e-meldung' }),
      el(
        'div',
        { class: 'knopfreihe' },
        el('button', { id: 'e-los', onclick: einrichtenKlick }, 'Konto einrichten'),
        el('button', { class: 'sekundaer', onclick: () => renderLogin() }, 'Zurück'),
      ),
    ),
  );

  if (vorbefuellt) {
    root.querySelector<HTMLInputElement>('#e-pw1')?.focus();
  }
}

async function einrichtenKlick(): Promise<void> {
  const email = root.querySelector<HTMLInputElement>('#e-email')!.value.trim();
  const code = root.querySelector<HTMLInputElement>('#e-code')!.value.trim();
  const pw1 = root.querySelector<HTMLInputElement>('#e-pw1')!.value;
  const pw2 = root.querySelector<HTMLInputElement>('#e-pw2')!.value;
  const meldung = root.querySelector<HTMLElement>('#e-meldung')!;
  const button = root.querySelector<HTMLButtonElement>('#e-los')!;

  clear(meldung);
  if (!email || !code) {
    meldung.append(hinweis('Bitte E-Mail-Adresse und Code eingeben.', 'warnung'));
    return;
  }
  if (pw1.length < 10) {
    meldung.append(hinweis('Bitte mindestens 10 Zeichen wählen.', 'warnung'));
    return;
  }
  if (pw1 !== pw2) {
    meldung.append(hinweis('Die beiden Eingaben stimmen nicht überein.', 'warnung'));
    return;
  }

  button.disabled = true;
  button.textContent = 'Schlüssel werden erzeugt …';
  try {
    const vorMe = await setupWithToken({
      email,
      setupToken: code,
      password: pw1,
      role: 'customer',
      withRecovery: true,
    });
    state.me = await api.get<Me>('/api/auth/me');
    if (vorMe.recoveryCode) renderRecoveryCode(vorMe.recoveryCode);
    else await zeigeAnsicht();
  } catch (error) {
    button.disabled = false;
    button.textContent = 'Konto einrichten';
    meldung.append(hinweis(fehlertext(error), 'warnung'));
  }
}

// ---------------------------------------------------------------------------
// Wiederherstellung
// ---------------------------------------------------------------------------

function renderWiederherstellung(meldung?: string): void {
  clear(root);
  root.append(
    el('h1', {}, el('span', { class: 'tb-wordmark' }, 'KF'), ' Akten-Safe'),
    karte(
      el('h2', {}, 'Passwort neu setzen'),
      el(
        'p',
        {},
        'Mit Ihrem Wiederherstellungs-Code – den zwölf Wörtern von der ' +
          'Einrichtung – können Sie ein neues Passwort festlegen. Ihre ' +
          'Dokumente bleiben dabei erhalten.',
      ),
      meldung ? hinweis(meldung, 'warnung') : null,
      el('label', { for: 'w-email' }, 'E-Mail-Adresse'),
      el('input', { type: 'email', id: 'w-email', autocomplete: 'username' }),
      el('label', { for: 'w-code' }, 'Wiederherstellungs-Code (zwölf Wörter)'),
      el('textarea', { id: 'w-code', rows: '3' }),
      el('label', { for: 'w-pw1' }, 'Neues Passwort'),
      el('input', { type: 'password', id: 'w-pw1', autocomplete: 'new-password' }),
      el('label', { for: 'w-pw2' }, 'Neues Passwort wiederholen'),
      el('input', { type: 'password', id: 'w-pw2', autocomplete: 'new-password' }),
      el('div', { id: 'w-meldung' }),
      el(
        'div',
        { class: 'knopfreihe' },
        el('button', { id: 'w-los', onclick: wiederherstellenKlick }, 'Passwort neu setzen'),
        el('button', { class: 'sekundaer', onclick: () => renderLogin() }, 'Zurück'),
      ),
    ),
  );
}

async function wiederherstellenKlick(): Promise<void> {
  const email = root.querySelector<HTMLInputElement>('#w-email')!.value.trim();
  const code = root.querySelector<HTMLTextAreaElement>('#w-code')!.value;
  const pw1 = root.querySelector<HTMLInputElement>('#w-pw1')!.value;
  const pw2 = root.querySelector<HTMLInputElement>('#w-pw2')!.value;
  const meldung = root.querySelector<HTMLElement>('#w-meldung')!;
  const button = root.querySelector<HTMLButtonElement>('#w-los')!;

  clear(meldung);
  if (pw1.length < 10) {
    meldung.append(hinweis('Bitte mindestens 10 Zeichen wählen.', 'warnung'));
    return;
  }
  if (pw1 !== pw2) {
    meldung.append(hinweis('Die beiden Eingaben stimmen nicht überein.', 'warnung'));
    return;
  }

  button.disabled = true;
  button.textContent = 'Wird geprüft …';
  try {
    await wiederherstellen({ email, recoveryCode: code, newPassword: pw1 });
    renderLogin('Passwort geändert. Bitte melden Sie sich damit an.');
  } catch (error) {
    button.disabled = false;
    button.textContent = 'Passwort neu setzen';
    meldung.append(hinweis(fehlertext(error), 'warnung'));
  }
}

// ---------------------------------------------------------------------------
// Notfallzugriff widersprechen (öffentlich, ohne Anmeldung)
// ---------------------------------------------------------------------------

/**
 * Eigenständige Seite ohne `kopfzeile()`: Es gibt keine Sitzung, der Token
 * aus der Mail ist der einzige Nachweis — Konzept wie beim Widerruf der
 * Konto-Selbstlöschung im Verwaltungssystem.
 */
function renderNotfallWiderspruch(token: string): void {
  clear(root);
  const status = el('div', { id: 'nw-status' });

  root.append(
    karte(
      el('h1', {}, el('span', { class: 'tb-wordmark' }, 'KF'), ' Notfallzugriff'),
      el(
        'p',
        {},
        'Jemand hat als Ihre hinterlegte Vertrauensperson Notfallzugriff auf Ihre ' +
          'Dokumente im Akten-Safe angefordert. Wenn das nicht in ' +
          'Ihrem Sinne ist, widersprechen Sie hier — der Zugriff wird dann nicht freigeschaltet.',
      ),
      status,
      el(
        'div',
        { class: 'knopfreihe' },
        el(
          'button',
          { id: 'nw-widersprechen', class: 'warnung', onclick: () => void widersprechenKlick(token, status) },
          'Zugriff nicht freischalten',
        ),
        el('button', { class: 'sekundaer', onclick: () => renderLogin() }, 'Nichts tun, zur Anmeldung'),
      ),
    ),
  );
}

async function widersprechenKlick(token: string, status: HTMLElement): Promise<void> {
  const button = root.querySelector<HTMLButtonElement>('#nw-widersprechen')!;
  button.disabled = true;
  clear(status);
  try {
    await notfallzugriffWidersprechen(token);
    clear(root);
    root.append(
      karte(
        el('h1', {}, el('span', { class: 'tb-wordmark' }, 'KF'), ' Notfallzugriff'),
        hinweis('Widerspruch eingegangen. Der Zugriff wird nicht freigeschaltet.', 'erfolg'),
        el('div', { class: 'knopfreihe' }, el('button', { onclick: () => renderLogin() }, 'Zur Anmeldung')),
      ),
    );
  } catch (error) {
    button.disabled = false;
    status.append(hinweis(fehlertext(error), 'warnung'));
  }
}

function renderRecoveryCode(code: string): void {
  clear(root);
  root.append(
    kopfzeile(),
    karte(
      el('h2', {}, 'Ihr Wiederherstellungs-Code'),
      el(
        'p',
        {},
        'Drucken Sie diese zwölf Wörter aus oder schreiben Sie sie ab und legen ' +
          'Sie den Zettel an einen sicheren Ort – zum Beispiel zu Ihren ' +
          'wichtigen Papieren. Nur damit können Sie wieder an Ihre Dokumente, ' +
          'wenn Sie das Passwort vergessen.',
      ),
      el('p', { class: 'recovery-code' }, code),
      hinweis(
        'Wir speichern diesen Code nirgends. Wir können ihn Ihnen später ' +
          'nicht noch einmal zeigen.',
        'warnung',
      ),
      el(
        'div',
        { class: 'knopfreihe' },
        el('button', { class: 'sekundaer', onclick: () => window.print() }, 'Ausdrucken'),
        el(
          'button',
          {
            onclick: () => {
              if (
                window.confirm(
                  'Haben Sie den Code notiert oder ausgedruckt? Er wird danach nicht mehr angezeigt.',
                )
              ) {
                navigiereZu('#/');
              }
            },
          },
          'Ich habe den Code sicher aufbewahrt',
        ),
      ),
    ),
  );
}

// ---------------------------------------------------------------------------
// Hilfe & FAQ
// ---------------------------------------------------------------------------

/**
 * FAQ-Beiträge als Datenstruktur, damit neue Einträge nur hier ergänzt
 * werden müssen. `antwort` liefert frische DOM-Knoten je Aufruf — Knoten
 * lassen sich nicht zweimal einhängen.
 */
/**
 * Adresse, die in Anleitungen genannt wird — bewusst FEST die neue Domain
 * (Rebranding 21.08.2026) statt `window.location.origin`: Wer noch über die
 * alte Adresse zugreift, soll sich die App trotzdem unter dem
 * künftigen Namen ablegen und nicht eine Adresse lernen, die später nur
 * noch weiterleitet.
 */
const APP_ADRESSE = 'https://akten.it-karsten-fischer.de';

const FAQ_EINTRAEGE: { frage: string; antwort: () => HTMLElement[] }[] = [
  {
    frage: 'Mehrere Safes: Mein Safe, Familie, fremde Safes – was ist das?',
    antwort: () => [
      el(
        'p',
        {},
        'Stellen Sie sich Aktenschränke vor. „Mein Safe" ist Ihr eigener Schrank – nur Sie haben den ' +
          'Schlüssel. Ein Familien-Safe ist ein gemeinsamer Schrank, zu dem jeder in der Familie einen ' +
          'Schlüssel hat. Und wenn Ihnen jemand aus der Familie einen Schlüssel zu seinem Safe gibt, ' +
          'sehen Sie dessen Schrank auch – „Tamaras Safe" zum Beispiel.',
      ),
      el(
        'p',
        {},
        'Umschalten müssen Sie nichts: Alle Schränke sind offen. Auf der Startseite wählen Sie nur, ' +
          'WO Sie suchen möchten (ein Safe oder „Überall"), beim Ablegen WOHIN. Jedes Dokument trägt ' +
          'ein Schildchen mit dem Namen seines Safes.',
      ),
      el(
        'p',
        {},
        'Einen Schlüssel zu Ihrem Safe geben Sie unter „Freigaben": Schalter bei der Person an – ' +
          'für alles oder nur für bestimmte Fächer, auf Wunsch mit „darf auch ablegen". Löschen kann in ' +
          'Ihrem Safe nur, wer ihn besitzt. Jeder Zugriff steht in Ihrem Protokoll, und der Schalter lässt ' +
          'sich jederzeit wieder ausschalten.',
      ),
    ],
  },
  {
    frage: 'Das Akten-Safe als App einrichten (Android)',
    antwort: () => [
      el(
        'p',
        {},
        'Sie können das Akten-Safe wie eine App mit eigenem Symbol auf dem ' +
          'Startbildschirm ablegen — ohne App-Store, es ist dieselbe sichere Anwendung.',
      ),
      el(
        'ol',
        {},
        el('li', {}, `Öffnen Sie ${APP_ADRESSE} im Browser Chrome.`),
        el('li', {}, 'Tippen Sie oben rechts auf das Menü mit den drei Punkten (⋮).'),
        el(
          'li',
          {},
          'Wählen Sie „App installieren“ — je nach Chrome-Version heißt der Punkt auch ' +
            '„Zum Startbildschirm hinzufügen“.',
        ),
        el('li', {}, 'Bestätigen Sie mit „Installieren“ bzw. „Hinzufügen“.'),
        el(
          'li',
          {},
          'Auf dem Startbildschirm erscheint das Symbol des Akten-Safes. Ab jetzt ' +
            'starten Sie es wie jede andere App.',
        ),
      ),
      el(
        'p',
        { class: 'leise' },
        'Nutzen Sie den Samsung-Internet-Browser: Menü (☰) unten rechts → „Seite hinzufügen ' +
          'zu“ → „Startbildschirm“. Die Anmeldung funktioniert in der App wie gewohnt; Ihr ' +
          'Passwort bleibt dabei immer auf Ihrem Gerät.',
      ),
    ],
  },
  {
    frage: 'Das Akten-Safe als App einrichten (iPhone, iPad & Mac)',
    antwort: () => [
      el(
        'p',
        {},
        'Auf iPhone und iPad legen Sie das Akten-Safe mit eigenem Symbol auf dem ' +
          'Home-Bildschirm ab — wichtig: Das geht nur im Browser Safari.',
      ),
      el(
        'ol',
        {},
        el('li', {}, `Öffnen Sie ${APP_ADRESSE} in Safari.`),
        el(
          'li',
          {},
          'Tippen Sie auf das Teilen-Symbol (Viereck mit Pfeil nach oben) — beim iPhone unten ' +
            'in der Mitte, beim iPad oben rechts neben der Adresszeile.',
        ),
        el(
          'li',
          {},
          'Scrollen Sie in der Liste nach unten und wählen Sie „Zum Home-Bildschirm“.',
        ),
        el('li', {}, 'Bestätigen Sie oben rechts mit „Hinzufügen“.'),
        el(
          'li',
          {},
          'Das Symbol erscheint auf dem Home-Bildschirm; das Akten-Safe öffnet sich ' +
            'darüber im Vollbild wie eine App.',
        ),
      ),
      el(
        'p',
        { class: 'leise' },
        'Am Mac geht es ähnlich: Seite in Safari öffnen, dann Teilen-Symbol → „Zum Dock ' +
          'hinzufügen“. Die Anmeldung funktioniert in der App wie gewohnt; Ihr Passwort ' +
          'bleibt dabei immer auf Ihrem Gerät.',
      ),
    ],
  },
];

function renderFaq(): void {
  clear(root);
  root.append(
    kopfzeile(),
    karte(
      el('h2', {}, 'Hilfe & FAQ'),
      el(
        'p',
        { class: 'leise' },
        'Kurze Anleitungen rund um das Akten-Safe. Tippen Sie eine Frage an, um die ' +
          'Antwort aufzuklappen. Fehlt etwas? Rufen Sie uns gern an.',
      ),
      el(
        'div',
        { class: 'faq-liste' },
        ...FAQ_EINTRAEGE.map((eintrag) =>
          el(
            'details',
            { class: 'faq-eintrag' },
            el('summary', {}, eintrag.frage),
            el('div', { class: 'faq-antwort' }, ...eintrag.antwort()),
          ),
        ),
      ),
      el(
        'div',
        { class: 'knopfreihe' },
        el('button', { class: 'sekundaer', onclick: () => navigiereZu('#/') }, '← Zurück'),
      ),
    ),
  );
}

// ---------------------------------------------------------------------------
// Abmelden
// ---------------------------------------------------------------------------

async function abmelden(): Promise<void> {
  try {
    await api.post('/api/auth/logout');
  } catch {
    // Auch wenn der Server nicht erreichbar ist, wird lokal aufgeräumt.
  }
  // Reihenfolge ist wichtig: erst die entschlüsselten Daten verwerfen.
  // Auch die Blob-URLs geöffneter Dokumente: Nach dem Abmelden soll kein
  // entschlüsseltes PDF mehr über eine noch lebende URL nachladbar sein.
  geoeffneteUrlsWiderrufen();
  datenVerwerfen();
  // Der Arbeiter der Texterkennung hält das zuletzt gelesene Bild in seinem
  // eigenen Speicher. Beim Abmelden hat das dort nichts mehr zu suchen.
  beendeAufbereitung();
  lockVault();
  kreisCacheLeeren();
  safesVerwerfen();
  setToken(null);
  state.me = null;
  renderLogin('Sie wurden abgemeldet. Ihr Suchindex wurde gelöscht.');
}

registriereAbmelden(() => void abmelden());

// Organisationswechsel wirft alle organisationsbezogenen Daten weg (andere
// Dokumente, anderer Ordnerbaum) und lädt `state.me` neu (andere kundeId,
// evtl. andere Rolle) — der Schlüssel selbst wurde schon in
// gemeinsam.ts/organisationen.ts entsperrt, bevor dieser Handler läuft.
registriereOrganisationsWechsel(async () => {
  datenVerwerfen();
  state.me = await api.get<Me>('/api/auth/me');
  await zeigeAnsicht();
});

// ---------------------------------------------------------------------------
// Blindablage (Buddy, Konzept 3.7 / 6.6.2)
// ---------------------------------------------------------------------------

/**
 * Eigene, enge Maske statt der normalen Übersicht: Eine Blindablage legt eine
 * Datei ab, ohne dass der Kunde etwas freigibt — die einzige Sicherung gegen
 * „beim falschen Kunden abgelegt" ist deshalb, dass hier gar nichts anderes
 * zu sehen und zu wählen ist als genau dieser eine, vom Verwaltungssystem
 * vorgegebene Kunde.
 *
 * Der öffentliche Schlüssel wird sofort beim Aufruf importiert (nicht erst
 * beim Ablegen): Ein defekter oder fehlender Schlüssel soll aussehen wie das,
 * was er ist — nicht wie ein Fehler mitten im Verschlüsseln, nachdem der
 * Buddy schon eine Datei gewählt hat.
 */
/** Dateifeld der Blindablage – von `blindAblegen()` gebraucht. */
let blindFeld: ReturnType<typeof ablegeFeld> | null = null;

async function renderBlindablage(ziel: BlindablageZiel): Promise<void> {
  clear(root);
  root.append(kopfzeile(), el('p', { class: 'loading' }, 'Empfänger wird geprüft …'));

  const zurueck = (): void => {
    navigiereZu('#/dokumente');
  };

  let empfaenger: { publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm };
  try {
    const antwort = await api.get<{ algorithm: KeyAlgorithm; publicKey: string }>(
      `/api/keys/customer/${ziel.customerId}/public`,
    );
    const publicKeyRaw = fromBase64(antwort.publicKey);
    await importPublicKey(publicKeyRaw, antwort.algorithm);   // frühes Scheitern bei defektem Schlüssel
    empfaenger = { publicKeyRaw, algorithm: antwort.algorithm };
  } catch (error) {
    clear(root);
    root.append(
      kopfzeile(),
      karte(
        el('h2', {}, 'Blindablage nicht möglich'),
        hinweis(
          `${fehlertext(error)} Möglich, dass Ihnen dieser Kunde nicht (mehr) ` +
            'zugeordnet ist oder er seinen Schlüssel noch nicht eingerichtet hat.',
          'warnung',
        ),
        el('div', { class: 'knopfreihe' }, el('button', { onclick: zurueck }, 'Zur Übersicht')),
      ),
    );
    return;
  }

  const status = el('div', { id: 'b-status' });

  const feld = ablegeFeld({
    id: 'b-datei',
    accept: '.pdf,.txt,.md,.csv,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.bmp,.tif,.tiff,.avif,image/*',
    beschriftung: 'Dateien auswählen (PDF, Fotos oder Textdateien)',
    aufforderung: 'Dateien hierher ziehen',
    erklaerung:
      'Mehrere Dateien werden nacheinander aufbereitet, verschlüsselt und ' +
      'abgelegt – alle für denselben Kunden.',
  });
  blindFeld = feld;

  const knopf = el(
    'button',
    { id: 'b-ablegen', onclick: () => void blindAblegen(status, ziel, empfaenger) },
    'Sicher ablegen',
  );
  feld.beiAenderung((dateien) => {
    knopf.textContent =
      dateien.length > 1 ? `${dateien.length} Dokumente sicher ablegen` : 'Sicher ablegen';
  });

  const kaestchen = (id: string, beschriftung: string): HTMLElement =>
    el(
      'div',
      { style: 'display:flex;gap:.6rem;align-items:center;margin:.9rem 0' },
      el('input', { type: 'checkbox', id, checked: true, style: 'width:auto;margin:0' }),
      el('label', { for: id, style: 'margin:0;font-weight:600' }, beschriftung),
    );

  clear(root);
  root.append(
    kopfzeile(),
    karte(
      el('h2', {}, 'Blindablage'),
      hinweis(
        `Sie legen jetzt eine Datei ab für: ${ziel.name ?? 'diesen Kunden'}. ` +
          'Der Kunde muss dazu nichts freigeben — die Datei wird in Ihrem Browser ' +
          'sofort mit seinem öffentlichen Schlüssel verschlüsselt, lesen kann sie ' +
          'nur er.',
      ),
      hinweis(
        `Vor dem Hochladen prüfen: ${ziel.name ?? '(kein Name übergeben)'}. ` +
          'Eine Datei beim falschen Kunden ist mit dessen Schlüssel verschlüsselt ' +
          '— er kann sie lesen, der richtige Empfänger nicht. Das ist eine ' +
          'meldepflichtige Datenschutzverletzung, und kein Rückruf macht sie ' +
          'ungeschehen. Bei mehreren Dateien gilt das für jede einzelne.',
        'warnung',
      ),
      feld.element,
      kaestchen('b-ki-opt-in', 'Automatisch einsortieren'),
      kaestchen('b-umbenennen', 'Dateiname vereinheitlichen'),
      status,
      el(
        'div',
        { class: 'knopfreihe' },
        knopf,
        el('button', { class: 'sekundaer', onclick: zurueck }, 'Zur Übersicht'),
      ),
    ),
  );
}

async function blindAblegen(
  status: HTMLElement,
  ziel: BlindablageZiel,
  empfaenger: { publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm },
): Promise<void> {
  const feld = blindFeld;
  const useAi = root.querySelector<HTMLInputElement>('#b-ki-opt-in')!.checked;
  const renameFile = root.querySelector<HTMLInputElement>('#b-umbenennen')!.checked;
  const button = root.querySelector<HTMLButtonElement>('#b-ablegen')!;
  const dateien = feld?.dateien() ?? [];

  clear(status);
  if (dateien.length === 0) {
    status.append(hinweis('Bitte wählen Sie zuerst eine Datei aus.', 'warnung'));
    return;
  }

  button.disabled = true;
  feld?.sperren(true);
  try {
    const bericht = await stapelVerarbeiten(dateien, status, async (datei, zeile) => {
      zeigeSchritt(zeile, 'Datei wird gelesen');
      const bytes = new Uint8Array(await datei.arrayBuffer());

      const aufbereitet = await aufbereiten(datei, bytes, zeile);
      if (!aufbereitet) return { art: 'uebersprungen', text: 'Es wurde nichts abgelegt.' };

      await uploadDocument({
        aufbereitet,
        useAi,
        renameFile,
        forCustomer: { customerId: ziel.customerId, ...empfaenger },
        onProgress: (schritt) => zeigeSchritt(zeile, schritt),
      });

      return {
        art: 'erfolg',
        text: `„${aufbereitet.dateiname}" wurde für ${ziel.name ?? 'den Kunden'} abgelegt.`,
        hinweise: aufbereitet.hinweise,
      } satisfies StapelErgebnis;
    });

    status.prepend(stapelMeldung(bericht));
    // Nur die tatsächlich abgelegten verschwinden aus der Auswahl. Was
    // übersprungen wurde oder fehlschlug, bleibt stehen und kann nach einer
    // Korrektur erneut abgeschickt werden – ohne dass dabei die schon
    // abgelegten ein zweites Mal hochgingen.
    feld?.entferne(bericht.erledigt);
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    button.disabled = false;
    feld?.sperren(false);
  }
}

// --- Admin -----------------------------------------------------------------

interface AdminKunde {
  id: string;
  email: string;
  display_name: string;
  buddy_account_id: string | null;
  buddy_name: string | null;
  key_setup_done: boolean;
  buddy_ready: boolean;
  document_count: number;
}

interface AdminBuddy {
  id: string;
  display_name: string;
  email: string;
  ready: boolean;
}

/**
 * Kundenverwaltung. Wichtigster Punkt hier ist die Zuordnung des zuständigen
 * Buddys: Ohne sie kann der Kunde keine Freigabe erteilen, und die Meldung,
 * die er dann sieht, kann er sich nicht erklären.
 */
async function ladeKundenverwaltung(): Promise<void> {
  const container = root.querySelector<HTMLElement>('#admin-kunden');
  if (!container) return;
  clear(container);

  const { customers, buddies } = await api.get<{
    customers: AdminKunde[];
    buddies: AdminBuddy[];
  }>('/api/admin/customers');

  const ohneBuddy = customers.filter((k) => !k.buddy_account_id).length;

  container.append(
    el(
      'div',
      {},
      el('h3', {}, `Kunden (${customers.length})`),
      ohneBuddy > 0
        ? hinweis(
            `${ohneBuddy} Kunde(n) ohne zuständigen Buddy. Diese können keine ` +
              'Freigabe erteilen, bis hier einer zugeordnet ist.',
            'warnung',
          )
        : null,
    ),
    el(
      'table',
      {},
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          el('th', {}, 'Kunde'),
          el('th', {}, 'Dokumente'),
          el('th', {}, 'Akten-Safe eingerichtet'),
          el('th', {}, 'Zuständiger Buddy'),
        ),
      ),
      el(
        'tbody',
        {},
        ...customers.map((kunde) => {
          const auswahl = el('select', { id: `buddy-${kunde.id}` }) as HTMLSelectElement;
          auswahl.append(
            el('option', { value: '' }, '— keiner —'),
            ...buddies.map((b) =>
              el(
                'option',
                {
                  value: b.id,
                  ...(b.id === kunde.buddy_account_id ? { selected: 'selected' } : {}),
                  ...(b.ready ? {} : { disabled: 'disabled' }),
                },
                b.ready
                  ? b.display_name
                  : `${b.display_name} (noch kein Schlüsselpaar)`,
              ),
            ),
          );
          auswahl.addEventListener('change', () => {
            void setzeBuddy(kunde.id, auswahl.value || null);
          });

          return el(
            'tr',
            {},
            el(
              'td',
              {},
              el('div', {}, kunde.display_name),
              el('div', { class: 'leise' }, kunde.email),
            ),
            el('td', {}, String(kunde.document_count)),
            el('td', {}, kunde.key_setup_done ? 'ja' : 'nein'),
            el(
              'td',
              {},
              auswahl,
              kunde.buddy_account_id && !kunde.buddy_ready
                ? el(
                    'div',
                    { class: 'leise' },
                    'Achtung: Dieser Buddy hat noch kein Schlüsselpaar.',
                  )
                : null,
            ),
          );
        }),
      ),
    ),
    el('div', { id: 'kunden-meldung' }),
  );
}

async function setzeBuddy(customerId: string, buddyAccountId: string | null): Promise<void> {
  const meldung = root.querySelector<HTMLElement>('#kunden-meldung');
  if (meldung) clear(meldung);
  try {
    await api.post(`/api/admin/customers/${customerId}/buddy`, { buddyAccountId });
    meldung?.append(hinweis('Zuordnung gespeichert.', 'erfolg'));
    await ladeKundenverwaltung();
  } catch (error) {
    meldung?.append(hinweis(fehlertext(error), 'warnung'));
  }
}

function renderAdmin(): void {
  clear(root);
  root.append(
    kopfzeile(),
    karte(
      el('h2', {}, 'Verwaltung'),
      hinweis(
        'Als Administrator sehen Sie ausschließlich Metadaten. Es gibt keine ' +
          'technische Möglichkeit, Dokumentinhalte zu entschlüsseln – das ist ' +
          'so gebaut und nicht abschaltbar.',
      ),
      el('div', { id: 'admin-kunden' }, el('p', { class: 'loading' }, 'Wird geladen …')),
    ),
    karte(el('div', { id: 'admin-historie' }, el('p', { class: 'loading' }, 'Wird geladen …'))),
  );

  void ladeKundenverwaltung();

  void (async () => {
    const container = root.querySelector<HTMLElement>('#admin-historie')!;
    const { entries } = await api.get<{
      entries: Array<{
        id: string;
        customer_name: string;
        buddy_name: string;
        granted_at: string;
        expires_at: string;
        revoked_at: string | null;
        document_count: number;
        ticket_reference: string | null;
      }>;
    }>('/api/admin/grant-history');

    clear(container);
    container.append(
      el('h3', {}, 'Freigabe-Historie'),
      el(
        'table',
        {},
        el(
          'thead',
          {},
          el(
            'tr',
            {},
            el('th', {}, 'Kunde'),
            el('th', {}, 'Buddy'),
            el('th', {}, 'Ticket'),
            el('th', {}, 'Erteilt'),
            el('th', {}, 'Läuft ab'),
            el('th', {}, 'Dokumente'),
          ),
        ),
        el(
          'tbody',
          {},
          ...entries.map((entry) =>
            el(
              'tr',
              {},
              el('td', {}, entry.customer_name),
              el('td', {}, entry.buddy_name),
              el('td', {}, entry.ticket_reference ?? '—'),
              el('td', {}, formatDate(entry.granted_at)),
              el('td', {}, entry.revoked_at ? 'zurückgezogen' : formatDate(entry.expires_at)),
              el('td', {}, String(entry.document_count)),
            ),
          ),
        ),
      ),
    );
  })();
}

// ---------------------------------------------------------------------------
// Start
// ---------------------------------------------------------------------------

/**
 * Löst das Einmal-Ticket aus dem Verwaltungssystem ein, bevor überhaupt eine
 * Anmeldemaske erscheint (Konzept 6.6.2). Gibt `true` zurück, wenn der Buddy
 * dadurch schon in der Blindablage steht — dann ist `start()` fertig.
 *
 * Bei jedem Fehlschlag (Ticket abgelaufen/verbraucht, Netzfehler) einfach
 * `false`: `start()` fällt dann auf die normale Anmeldung zurück, die dank
 * `blindablageZielKunde` nach dem Login automatisch hierher zurückkehrt.
 */
async function blindablageAutomatischAnmelden(ziel: BlindablageZiel): Promise<boolean> {
  if (!ziel.einloeseToken) return false;
  try {
    const antwort = await api.post<{ token: string }>('/api/auth/blindablage-einloesen', {
      token: ziel.einloeseToken,
    });
    setToken(antwort.token);
    state.me = await api.get<Me>('/api/auth/me');
    await renderBlindablage(ziel);
    return true;
  } catch {
    setToken(null);
    return false;
  }
}

/**
 * Kommt der Akten-Safe aus dem Kundenportal, hat der Kunde sein Passwort
 * dort schon eingegeben. Statt ihn ein zweites Mal danach zu fragen, nehmen
 * wir die fertigen Schlüssel entgegen (siehe uebergabe.ts).
 *
 * Schlägt das fehl — Frist abgelaufen, Schlüssel passen nicht, Konto weg —
 * fällt der Ablauf auf die gewohnte Passwortmaske zurück. Die Abkürzung darf
 * nie der einzige Weg hinein sein.
 *
 * Bis 01.09.2026 lief dieser Weg fälschlich in `ladeKonto()`, das Kunden
 * grundsätzlich zur Anmeldung schickt („Schlüssel nach Neuladen weg") — die
 * Übergabe endete damit trotz gültiger Schlüssel wieder an der Passwortmaske.
 * Jetzt geht es mit dem frisch entsperrten Schlüssel direkt in die Ansicht.
 */
async function uebergabeVersuchen(): Promise<boolean> {
  if (!istEingebettet()) return false;
  const uebergabe = await aufUebergabeWarten();
  if (!uebergabe) return false;

  try {
    await anmeldenMitUebergabe(uebergabe.email, uebergabe.anmeldebeweis, uebergabe.schluessel);
    state.me = await api.get<Me>('/api/auth/me');
    await zeigeAnsicht();
    return true;
  } catch {
    setToken(null);
    lockVault();
    kreisCacheLeeren();
    return false;
  }
}

async function start(): Promise<void> {
  if (notfallWiderspruch) {
    renderNotfallWiderspruch(notfallWiderspruch);
    return;
  }

  // Vor allen anderen Wegen: Wenn uns das Portal einbettet, wartet dort schon
  // ein entsperrter Akten-Safe auf uns.
  if (await uebergabeVersuchen()) return;

  if (blindablageZielKunde && (await blindablageAutomatischAnmelden(blindablageZielKunde))) {
    return;
  }

  if (personalEinloeseToken) {
    try {
      const antwort = await api.post<{ token: string }>('/api/auth/blindablage-einloesen', {
        token: personalEinloeseToken,
      });
      setToken(antwort.token);
      await ladeKonto();
      return;
    } catch {
      renderLogin('Der automatische Zugang ist abgelaufen oder schon benutzt. Bitte einmal anmelden.');
      return;
    }
  }

  if (!getToken()) {
    if (einrichtung) renderEinrichtung(undefined, einrichtung);
    else {
      renderLogin(
        blindablageZielKunde
          ? 'Der automatische Zugang ist abgelaufen. Bitte einmal anmelden — Sie landen ' +
              'danach direkt in der Blindablage.'
          : einladungZiel
            ? 'Sie wurden zu einer Organisation eingeladen. Bitte melden Sie sich mit Ihrem ' +
                'bestehenden Akten-Safe-Konto an, um die Einladung anzunehmen.'
            : undefined,
      );
    }
    return;
  }
  try {
    await ladeKonto();
  } catch {
    setToken(null);
    renderLogin();
  }
}

// Ein Neuladen der Seite verwirft den privaten Schlüssel bewusst – die
// Passwortabfrage danach ist kein Fehler, sondern Absicht.
if (currentVault() === null) {
  void start();
}
